import { Link } from "react-router-dom";
import {
  Car,
  Check,
  X,
  Zap,
  Building2,
  Crown,
  ArrowRight,
  Shield,
  Clock,
  FileText,
  Users,
} from "lucide-react";
import { Header } from "../components/Header";

interface PricingTier {
  name: string;
  price: number;
  period: string;
  description: string;
  features: { text: string; included: boolean }[];
  popular?: boolean;
  cta: string;
  icon: React.ReactNode;
  color: string;
}

const pricingTiers: PricingTier[] = [
  {
    name: "Starter",
    price: 0,
    period: "month",
    description: "Perfect starting package for individual users",
    icon: <Zap className="w-6 h-6" />,
    color: "emerald",
    cta: "Get Started Free",
    features: [
      { text: "5 analyses per month", included: true },
      { text: "Basic damage detection", included: true },
      { text: "PDF report download", included: true },
      { text: "Email support", included: true },
      { text: "Fault analysis", included: false },
      { text: "Cost estimation", included: false },
      { text: "API access", included: false },
      { text: "Priority support", included: false },
    ],
  },
  {
    name: "Professional",
    price: 499,
    period: "month",
    description: "For lawyers and insurance consultants",
    icon: <Crown className="w-6 h-6" />,
    color: "blue",
    popular: true,
    cta: "Upgrade to Pro",
    features: [
      { text: "50 analyses per month", included: true },
      { text: "Advanced damage detection", included: true },
      { text: "PDF & Word reports", included: true },
      { text: "Priority email support", included: true },
      { text: "Fault analysis (legally compliant)", included: true },
      { text: "Detailed cost estimation", included: true },
      { text: "API access", included: false },
      { text: "Custom integration", included: false },
    ],
  },
  {
    name: "Enterprise",
    price: 1999,
    period: "month",
    description: "For insurance companies and large teams",
    icon: <Building2 className="w-6 h-6" />,
    color: "purple",
    cta: "Contact Sales",
    features: [
      { text: "Unlimited analyses", included: true },
      { text: "Most advanced AI model", included: true },
      { text: "All format support", included: true },
      { text: "24/7 phone support", included: true },
      { text: "Full legal analysis package", included: true },
      { text: "Market price integration", included: true },
      { text: "REST API access", included: true },
      { text: "Custom integration & training", included: true },
    ],
  },
];

const stats = [
  { value: "10,000+", label: "Analyses Completed" },
  { value: "98%", label: "Accuracy Rate" },
  { value: "2 min", label: "Average Time" },
  { value: "500+", label: "Happy Customers" },
];

export function PricingPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20">
        {/* Background Effects */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Simple, Transparent
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">
              Pricing
            </span>
          </h1>
          
          <p className="text-xl text-white/70 max-w-2xl mx-auto mb-10">
            AI revolution in insurance damage analysis. Suitable plans for everyone,
            from individual users to enterprise companies.
          </p>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white">{stat.value}</div>
                <div className="text-white/60 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 -mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {pricingTiers.map((tier, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-transform hover:scale-105 ${
                  tier.popular ? "ring-2 ring-blue-500" : ""
                }`}
              >
                {tier.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-blue-500 text-white text-center py-2 text-sm font-medium">
                    Most Popular
                  </div>
                )}

                <div className={`p-8 ${tier.popular ? "pt-14" : ""}`}>
                  {/* Icon & Name */}
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        tier.color === "emerald"
                          ? "bg-emerald-100 text-emerald-600"
                          : tier.color === "blue"
                          ? "bg-blue-100 text-blue-600"
                          : "bg-purple-100 text-purple-600"
                      }`}
                    >
                      {tier.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{tier.name}</h3>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-gray-900">
                        {tier.price === 0 ? "Free" : `$${tier.price}`}
                      </span>
                      {tier.price > 0 && (
                        <span className="text-gray-500">/{tier.period}</span>
                      )}
                    </div>
                    <p className="text-gray-600 mt-2">{tier.description}</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-3">
                        {feature.included ? (
                          <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-emerald-600" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <X className="w-3 h-3 text-gray-400" />
                          </div>
                        )}
                        <span
                          className={
                            feature.included ? "text-gray-700" : "text-gray-400"
                          }
                        >
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link
                    to={tier.price === 0 ? "/signup" : "/signup"}
                    className={`w-full py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                      tier.popular
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : tier.color === "purple"
                        ? "bg-purple-600 hover:bg-purple-700 text-white"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-900"
                    }`}
                  >
                    {tier.cta}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Features Included in All Plans
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Whichever plan you choose, you'll benefit from these core features
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-slate-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Secure Data</h3>
              <p className="text-gray-600 text-sm">
                Your data is safe with 256-bit SSL encryption
              </p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-slate-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Fast Analysis</h3>
              <p className="text-gray-600 text-sm">
                Detailed report in an average of 2 minutes
              </p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-7 h-7 text-slate-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">PDF & Images</h3>
              <p className="text-gray-600 text-sm">
                PDF documents automatically converted to images
              </p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-slate-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Multi-language</h3>
              <p className="text-gray-600 text-sm">
                Full interface and reports in multiple languages
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Get Started Today
          </h2>
          <p className="text-xl text-white/70 mb-8">
            Try Insura AI with the free plan. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="px-8 py-4 bg-white text-slate-900 rounded-xl font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              Create Free Account
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/login"
              className="px-8 py-4 bg-white/10 text-white rounded-xl font-semibold hover:bg-white/20 transition-colors backdrop-blur-sm"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-slate-900">Insura AI</span>
            </div>
            <p className="text-gray-500 text-sm">
              © 2024 Insura AI. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-500 hover:text-gray-700 text-sm">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-500 hover:text-gray-700 text-sm">
                Terms of Service
              </a>
              <a href="#" className="text-gray-500 hover:text-gray-700 text-sm">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
